
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface KeyTrait {
  id: string;
  factor: 'dominance' | 'extraversion' | 'patience' | 'formality' | 'objectivity';
  level: 'high' | 'low';
  trait_en: string;
  trait_pt: string;
  trait_es: string;
}

interface UseKeyTraitsReturn {
  getTraitForFactor: (factor: string, value: number) => string;
  loading: boolean;
  error: string | null;
}

export const useKeyTraits = (): UseKeyTraitsReturn => {
  const { language } = useLanguage();
  const [traits, setTraits] = useState<KeyTrait[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTraits = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('key_traits')
          .select('*');

        if (error) {
          console.error('Error fetching key traits:', error);
          setError(error.message);
          return;
        }

        // Type assert the data to match our interface since we know the database constraints ensure valid values
        setTraits((data || []) as KeyTrait[]);
      } catch (err) {
        console.error('Error in fetchTraits:', err);
        setError('Failed to fetch traits');
      } finally {
        setLoading(false);
      }
    };

    fetchTraits();
  }, []);

  const getTraitForFactor = (factor: string, value: number): string => {
    // Determine if the value is high (>50) or low (<=50)
    const level = value > 50 ? 'high' : 'low';
    
    // Find the trait for this factor and level
    const trait = traits.find(t => t.factor === factor && t.level === level);
    
    if (!trait) {
      console.warn(`No trait found for factor: ${factor}, level: ${level}`);
      return factor; // Fallback to factor name
    }

    // Return the trait in the current language
    switch (language) {
      case 'pt':
        return trait.trait_pt;
      case 'es':
        return trait.trait_es;
      default:
        return trait.trait_en;
    }
  };

  return {
    getTraitForFactor,
    loading,
    error
  };
};
